/**
 * Pure domain vocabulary for the supporter → decision maker outreach flow.
 *
 * Lives in `utils/` rather than alongside the components because `shared/hooks` needs it too, and the
 * hooks layer is not allowed to import the components layer. Anything here must stay free of React.
 */

/* eslint-disable internal-rules/no-invalid-shared-folder-exports -- the reason ids are the domain
   vocabulary this module exists to hold, and the type below is derived from the value, so the two
   cannot be separated. */

import type { I18n } from '@change-corgi/core/i18n';

/**
 * The five reason cards from the component spec (§Screen 2). `own` ("Something else") is the
 * full-width tile and is the only one that carries no prompt.
 */
export const CONTACT_DECISION_MAKER_REASON_IDS = ['affects', 'care', 'community', 'right', 'own'] as const;

export type ContactDecisionMakerReasonId = (typeof CONTACT_DECISION_MAKER_REASON_IDS)[number];

/**
 * Whether the draft came from the LLM or from the deterministic template the middle-end falls back
 * to. Surfaced so the fallback rate is measurable — it is not shown to the supporter.
 */
export type ContactDecisionMakerDraftSource = 'llm' | 'fallback';

/**
 * How a send failed, in the terms the review screen has to act on:
 *
 * - `retryable` — transport, or a failure the resolver could not classify. The same body may work.
 * - `rejected` — the message as written is not acceptable (blank, too long). Editing it helps.
 * - `blocked` — nothing about the message is wrong: the session expired, or the petition is gone.
 *   Editing cannot help, so offering an edit-and-retry would loop the supporter forever.
 * - `unconfirmed` — the request was abandoned before an answer came back, so whether the message was
 *   stored is unknown. The mutation carries no idempotency key and `createComment` does not dedupe,
 *   so a retry here would deliver a second message to the decision maker.
 *
 * Here rather than beside either consumer: the hook that classifies the resolver's reason and the
 * stage machine that renders the outcome both need it, and `hooks` may not import `components`.
 */
export type ContactDecisionMakerSendFailure = 'retryable' | 'rejected' | 'blocked' | 'unconfirmed';

/** The i18n package is React-free, so importing its `translate` type keeps this module React-free too. */
type SeedDraftTranslate = I18n['translate'];

/**
 * The salutation-plus-story shape the spec seeds the draft with: `Dear {DM},\n\n{story}\n\n`.
 *
 * Used by two paths that need to look identical to the supporter: "Continue with my own words", which
 * skips drafting entirely, and the client-side fallback when the draft mutation cannot be reached at
 * all. Adds nothing of its own — a template that asserted something on the supporter's behalf would
 * be worse than one that just frames their words.
 *
 * The salutation is translated rather than built here: this text is shown to the supporter and sent
 * to the decision maker, so an English "Dear" on an otherwise localised letter is a user-visible bug.
 * `translate` is passed in to keep this module free of React, per the note at the top of the file.
 *
 * OPEN QUESTION (CHANGE-97532): the trailing newlines are a cursor affordance from when this seeded
 * an editable textarea. Since the 2026-09-01 CTA decision both paths land on the read-only review
 * instead, where a bare salutation and trailing blank lines read as an unfinished draft. What the
 * card should show here is Janice's call — guessing makes a visible copy bug, so the shape is
 * unchanged.
 */
export function seedDraft(translate: SeedDraftTranslate, decisionMakerName: string, story: string): string {
	return translate('corgi.contact_decision_maker.story.own_words_draft', {
		decision_maker_name: decisionMakerName,
		story: story.trim(),
	});
}
